<template>
    <div class="c-upload-later" :style="wrapStyle">
        <div :style="innerStyle">
            <div :style="headerStyle">
                <div :style="eyebrowRowStyle">
                    <div :style="eyebrowStyle">
                        Upload later · {{ (deal_name || agencyName || '').toUpperCase() }}
                    </div>
                    <div v-if="totalPagesOfSOW > 1" :style="paginationBadgeStyle">
                        SOW {{ currectPageOfSOW }} of {{ totalPagesOfSOW }}
                    </div>
                </div>
                <h1 :style="h1Style">Finish uploading the SOW.</h1>
                <p :style="leadStyle">
                    We held a spot for this deal earlier. Now upload or link the
                    signed document — or tell us if you still don't have it.
                </p>
            </div>

            <!-- ===== Top card: identity + dates ===== -->
            <CCard :extra-style="topCardStyle">
                <div :style="topGridStyle">
                    <div :style="topLeftStyle">
                        <div>
                            <label :style="fieldLabelStyle">
                                Title <span :style="requiredMark">*</span>
                            </label>
                            <div :style="inputWrapStyle">
                                <input
                                    type="text"
                                    id="sow_title"
                                    v-model="new_sow.title"
                                    name="sow_title"
                                    :style="bareInputStyle"
                                />
                            </div>
                            <span v-if="errors.title" :style="fieldErrorStyle">{{ errors.title }}</span>
                        </div>
                    </div>

                    <div :style="topRightStyle">
                        <div>
                            <label :style="fieldLabelStyle">
                                Activation date <span :style="requiredMark">*</span>
                            </label>
                            <div
                                class="form-field form-field--datepicker"
                                ref="datepicker"
                                :style="datepickerWrapStyle"
                            >
                                <input
                                    type="text"
                                    id="sow_date"
                                    v-model="new_sow.date"
                                    name="sow_date"
                                    placeholder="mm/dd/yyyy"
                                    :style="datepickerInputStyle"
                                    @changeDate="onChangeDatePicker"
                                />
                                <button type="button" class="btn btn--icon" :style="datepickerBtnStyle">
                                    <i class="icon icon-calendar"></i>
                                </button>
                            </div>
                            <span v-if="errors.date" :style="fieldErrorStyle">{{ errors.date }}</span>
                        </div>

                        <div>
                            <label :style="fieldLabelStyle">
                                Valid until <span :style="optionalBadge">optional</span>
                            </label>
                            <div
                                class="form-field form-field--datepicker"
                                ref="valid_datepicker"
                                :style="datepickerWrapStyle"
                            >
                                <input
                                    type="text"
                                    id="valid_date"
                                    v-model="new_sow.valid_date"
                                    name="valid_date"
                                    placeholder="mm/dd/yyyy"
                                    :style="datepickerInputStyle"
                                    @changeDate="onChangeValidDatePicker"
                                />
                                <button type="button" class="btn btn--icon" :style="datepickerBtnStyle">
                                    <i class="icon icon-calendar"></i>
                                </button>
                            </div>
                            <span v-if="errors.valid_date" :style="fieldErrorStyle">{{ errors.valid_date }}</span>
                        </div>
                    </div>
                </div>
            </CCard>

            <!-- ===== Two parallel cards: Signed vs Not signed ===== -->
            <div :style="dualCardGridStyle">
                <!-- Signed (File / URL / Later sub-rows) -->
                <CCard :extra-style="signedCardStyle(is_has_signed_sow === true)">
                    <div
                        :style="signedHeaderStyle"
                        @click="is_has_signed_sow = true"
                    >
                        <span :style="cRadioDotStyle(is_has_signed_sow === true)">
                            <span v-if="is_has_signed_sow === true" :style="cRadioInnerStyle" />
                        </span>
                        <div>
                            <div :style="signedTitleStyle">I have a signed SOW</div>
                            <div :style="signedSubStyle">Attach the document or link, or upload it later.</div>
                        </div>
                        <input
                            type="radio"
                            name="is_has_signed_sow"
                            id="is_has_signed_sow_true"
                            :checked="is_has_signed_sow === true"
                            :style="srOnly"
                            @change="is_has_signed_sow = true"
                        />
                    </div>

                    <div v-if="is_has_signed_sow === true" :style="signedRowsStyle">
                        <!-- File row -->
                        <div :style="proofRowStyle(is_file_option === true)">
                            <span
                                :style="cRadioDotStyle(is_file_option === true)"
                                @click="is_file_option = true"
                            >
                                <span v-if="is_file_option === true" :style="cRadioInnerStyle" />
                            </span>
                            <div :style="proofRowBodyStyle">
                                <div :style="proofRowLabelStyle">File</div>
                                <div v-if="is_file_option === true && new_sow.filename" :style="proofRowMetaStyle">
                                    {{ new_sow.filename }}
                                    <a href="#" :style="proofRowRemoveStyle" @click.prevent="new_sow.filename = ''; new_sow.file = null;">remove</a>
                                </div>
                                <div v-else :style="proofRowMetaStyle">PDF, DOCX up to 20 MB</div>
                            </div>
                            <input
                                type="radio"
                                name="is_file"
                                id="is_file_true"
                                :checked="is_file_option === true"
                                :style="srOnly"
                                @change="is_file_option = true"
                            />
                            <div
                                class="form-field form-field--file"
                                ref="filepicker"
                                :style="filePickerWrapStyle"
                            >
                                <input
                                    type="file"
                                    id="agreement"
                                    :disabled="!is_file_option_computed"
                                    name="agreement"
                                    hidden
                                    ref="fileInput"
                                    @input="pickFile"
                                    @change="pickFile"
                                />
                                <button
                                    type="button"
                                    class="btn btn--file"
                                    :style="filePickerBtnStyle"
                                >
                                    {{ new_sow.filename && is_file_option === true ? 'Replace' : 'Choose file' }} +
                                </button>
                            </div>
                            <span v-if="errors.file" :style="fieldErrorStyle">{{ errors.file }}</span>
                        </div>

                        <!-- URL row -->
                        <div :style="proofRowStyle(is_file_option === false)">
                            <span
                                :style="cRadioDotStyle(is_file_option === false)"
                                @click="is_file_option = false"
                            >
                                <span v-if="is_file_option === false" :style="cRadioInnerStyle" />
                            </span>
                            <div :style="proofRowBodyStyle">
                                <div :style="proofRowLabelStyle">URL</div>
                                <div v-if="is_file_option !== false" :style="proofRowMetaStyle">
                                    Paste a link to the signed SOW
                                </div>
                                <div v-else :style="urlInputWrapStyle">
                                    <input
                                        type="text"
                                        id="sow_url"
                                        :disabled="is_file_option_computed"
                                        v-model="new_sow.url"
                                        name="sow_url"
                                        placeholder="https://docs.google.com/…"
                                        :style="bareInputStyle"
                                    />
                                </div>
                                <span v-if="errors.url" :style="fieldErrorStyle">{{ errors.url }}</span>
                            </div>
                            <input
                                type="radio"
                                name="is_file"
                                id="is_file_false"
                                :checked="is_file_option === false"
                                :style="srOnly"
                                @change="is_file_option = false"
                            />
                        </div>

                        <!-- Upload later row -->
                        <div
                            :style="proofRowStyle(is_file_option === 'later')"
                            @click="is_file_option = 'later'"
                        >
                            <span :style="cRadioDotStyle(is_file_option === 'later')">
                                <span v-if="is_file_option === 'later'" :style="cRadioInnerStyle" />
                            </span>
                            <div :style="proofRowBodyStyle">
                                <div :style="proofRowLabelStyle">Upload later</div>
                                <div :style="proofRowMetaStyle">We'll remind you next period.</div>
                            </div>
                            <input
                                type="radio"
                                name="is_upload_later"
                                id="is_upload_later"
                                :checked="is_file_option === 'later'"
                                :style="srOnly"
                                @change="is_file_option = 'later'"
                            />
                        </div>
                    </div>
                </CCard>

                <!-- Unsigned -->
                <CCard :extra-style="signedCardStyle(is_has_signed_sow === false)">
                    <div
                        :style="signedHeaderStyle"
                        @click="is_has_signed_sow = false"
                    >
                        <span :style="cRadioDotStyle(is_has_signed_sow === false)">
                            <span v-if="is_has_signed_sow === false" :style="cRadioInnerStyle" />
                        </span>
                        <div>
                            <div :style="signedTitleStyle">No signed SOW yet</div>
                            <div :style="signedSubStyle">Help us understand why so we can follow up.</div>
                        </div>
                        <input
                            type="radio"
                            name="is_has_signed_sow"
                            id="is_has_signed_sow_false"
                            :checked="is_has_signed_sow === false"
                            :style="srOnly"
                            @change="is_has_signed_sow = false"
                        />
                    </div>

                    <div v-if="is_has_signed_sow === false" :style="explanationFieldStyle">
                        <label :style="fieldLabelStyle">
                            Explanation <span :style="requiredMark">*</span>
                        </label>
                        <textarea
                            id="explanation"
                            :disabled="is_has_signed_sow"
                            v-model="new_sow.description"
                            name="explanation"
                            :style="textareaStyle"
                            placeholder="e.g. Client agreed verbally, paperwork in legal review…"
                        ></textarea>
                        <span v-if="errors.description" :style="fieldErrorStyle">{{ errors.description }}</span>
                    </div>
                </CCard>
            </div>

            <!-- ===== Bottom nav ===== -->
            <div :style="navStyle">
                <CBtn ghost @click="onPrevSection">← Previous</CBtn>
                <span :style="navHintStyle">
                    <template v-if="totalPagesOfSOW > 1">
                        {{ currectPageOfSOW }} of {{ totalPagesOfSOW }} • autosaved as you go
                    </template>
                    <template v-else>autosaved as you go</template>
                </span>
                <CBtn primary big :disabled="isLoading" @click="onSaveSow">Next →</CBtn>
            </div>

            <div v-if="isLoading" :style="loaderStyle">
                <clip-loader :loading="isLoading" color="#ec4657" size="45px" />
            </div>
        </div>
    </div>
</template>

<script>
import ClipLoader from "vue-spinner/src/ClipLoader";

import CurrencyInput from "../../../components/CurrencyInput";
import { Datepicker } from "vanillajs-datepicker";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { scrollToTop } from "../../../helpers/_scrollToTop";
import { SECTIONS } from "../consts";

import CCard from "../c-design/CCard.vue";
import CBtn from "../c-design/CBtn.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_BG,
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    C_RED,
    FONT_DISPLAY,
    FONT_BODY,
    navMobileStickyStyle,
} from "../c-design/tokens";

export default {
    name: "Wizard_Upload_Later_SOW",
    mixins: [viewportMixin],
    props: ["onChangeSection"],
    data() {
        return {
            SECTIONS,
            isLoading: true,
            is_has_signed_sow: true,
            deal_name: "",
            sow_id: null,
            deal_id: null,
            deal_line_item_id: null,
            service_name: "",
            is_file_option: true,
            is_upload_later: false,
            new_sow: {
                title: "",
                date: "",
                file: null,
                url: "",
                filename: "",
                valid_date: "",
                description: "",
            },
            errors: {
                title: null,
                date: null,
                valid_date: null,
                file: null,
                url: null,
                type: null,
                description: null,
            },
            totalPagesOfSOW: 1,
            currectPageOfSOW: 1,
            dealLineItemsWithouSOWs: null,
            urlRegex:
                /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/,
            is_from_query: false,
        };
    },
    components: {
        Treeselect,
        ClipLoader,
        CurrencyInput,
        CCard,
        CBtn,
    },

    created() {
        this.getData();
    },

    mounted() {
        this.initDatePicker();
        this.initValidDatePicker();
        this.initFilePicker();
    },
    computed: {
        agencyName() {
            return this.$store.getters.getAgencyName;
        },
        isHasKickOff() {
            return this.$store.getters.getIsHasKickOff;
        },
        is_file_option_computed() {
            if (this.is_has_signed_sow) {
                if (this.is_file_option) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        },
        sowIndex() {
            return this.$store.getters.getCurrentIndexSOW;
        },

        getInitParamsFromQuery() {
            return this.$store.getters.getInitUploadLaterSOWFromQuery;
        },
        // ===== c-design style maps =====
        wrapStyle() {
            return {
                background: C_BG,
                minHeight: '70vh',
                padding: this.isMobile ? '14px 12px 110px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return { maxWidth: '980px', margin: '0 auto' };
        },
        headerStyle() {
            return {
                marginBottom: this.isMobile ? '14px' : '20px',
                padding: this.isMobile ? '0 4px' : '0 8px',
            };
        },
        eyebrowRowStyle() {
            return {
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '12px',
            };
        },
        eyebrowStyle() {
            return {
                fontSize: '11px', color: C_MUTED, letterSpacing: '0.8px',
                textTransform: 'uppercase', fontWeight: 700,
            };
        },
        paginationBadgeStyle() {
            return {
                padding: '4px 10px',
                background: '#fff',
                border: `1px solid ${C_HAIR}`,
                borderRadius: '999px',
                fontSize: '11px', color: C_INK2,
                fontWeight: 600, letterSpacing: '0.2px',
            };
        },
        h1Style() {
            return {
                margin: '8px 0 8px', fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '28px' : '36px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-0.8px' : '-1px',
                lineHeight: 1.1, color: C_INK,
            };
        },
        leadStyle() {
            return {
                margin: 0,
                fontSize: this.isMobile ? '13.5px' : '15px',
                color: C_MUTED,
                lineHeight: 1.55,
                maxWidth: this.isMobile ? 'none' : '640px',
            };
        },
        topCardStyle() {
            return {
                marginTop: '6px',
                marginBottom: this.isMobile ? '12px' : '16px',
                padding: this.isMobile ? '22px' : '28px 32px',
            };
        },
        topGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 280px',
                gap: this.isMobile ? '18px' : '24px',
                alignItems: 'start',
            };
        },
        topLeftStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '16px' };
        },
        topRightStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '16px' };
        },
        fieldLabelStyle() {
            return {
                display: 'flex', alignItems: 'baseline', gap: '8px',
                marginBottom: '7px', fontSize: '12px', color: C_INK2,
                fontWeight: 600, letterSpacing: '0.1px',
            };
        },
        requiredMark() {
            return { color: C_RED, marginLeft: '3px' };
        },
        optionalBadge() {
            return { fontSize: '11px', color: C_MUTED, fontWeight: 400 };
        },
        inputWrapStyle() {
            return {
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, padding: '0 14px',
            };
        },
        bareInputStyle() {
            return {
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', padding: '14px 0',
                fontSize: '16px', fontFamily: FONT_BODY,
                fontWeight: 500, color: C_INK, width: '100%',
            };
        },
        urlInputWrapStyle() {
            return {
                marginTop: '6px',
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, padding: '0 12px',
            };
        },
        datepickerWrapStyle() {
            return {
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, padding: '0 12px',
            };
        },
        datepickerInputStyle() {
            return {
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', padding: '14px 0',
                fontSize: '14px', fontFamily: FONT_BODY,
                color: C_INK, width: '100%',
            };
        },
        datepickerBtnStyle() {
            return {
                background: 'transparent', border: 'none',
                padding: '6px 4px', margin: '0 0 0 4px',
                fontSize: '16px', color: C_MUTED, cursor: 'pointer',
            };
        },
        srOnly() {
            return {
                position: 'absolute', width: '1px', height: '1px',
                margin: '-1px', overflow: 'hidden',
                clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: '0',
            };
        },
        cRadioInnerStyle() {
            return {
                width: '11px', height: '11px', borderRadius: '50%',
                background: C_INK, display: 'inline-block',
            };
        },
        dualCardGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '12px' : '16px',
                marginTop: '4px',
            };
        },
        signedHeaderStyle() {
            return {
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                cursor: 'pointer',
            };
        },
        signedTitleStyle() {
            return {
                fontFamily: FONT_DISPLAY, fontSize: '17px', fontWeight: 700,
                letterSpacing: '-0.2px', color: C_INK,
            };
        },
        signedSubStyle() {
            return { fontSize: '12px', color: C_MUTED, marginTop: '1px' };
        },
        signedRowsStyle() {
            return {
                marginTop: '18px',
                display: 'flex', flexDirection: 'column', gap: '10px',
            };
        },
        proofRowBodyStyle() {
            return { flex: 1, minWidth: 0 };
        },
        proofRowLabelStyle() {
            return { fontSize: '13px', fontWeight: 600, color: C_INK };
        },
        proofRowMetaStyle() {
            return {
                fontSize: '12px', color: C_MUTED, marginTop: '2px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            };
        },
        proofRowRemoveStyle() {
            return {
                color: C_INK, marginLeft: '6px',
                textDecoration: 'underline', textUnderlineOffset: '2px',
            };
        },
        filePickerWrapStyle() {
            return { display: 'flex', alignItems: 'center', gap: '8px' };
        },
        filePickerBtnStyle() {
            return {
                padding: '8px 14px', borderRadius: '10px',
                border: `1.5px solid ${C_HAIR}`, background: '#fff',
                fontFamily: FONT_BODY, fontSize: '13px', fontWeight: 600,
                color: C_INK, cursor: 'pointer',
            };
        },
        explanationFieldStyle() {
            return { marginTop: '14px' };
        },
        textareaStyle() {
            return {
                width: '100%', minHeight: '140px', padding: '14px',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, resize: 'vertical',
                fontFamily: FONT_BODY, fontSize: '14px',
                color: C_INK, outline: 'none', lineHeight: 1.55,
            };
        },
        fieldErrorStyle() {
            return {
                display: 'block', marginTop: '6px',
                fontSize: '12px', color: C_RED, fontWeight: 500,
            };
        },
        navStyle() {
            return navMobileStickyStyle(this.isMobile, {
                marginTop: '14px', padding: '16px 24px',
                background: '#fff', borderRadius: '16px',
                border: `1px solid ${C_HAIR}`,
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '16px',
            });
        },
        navHintStyle() {
            return { fontSize: '12px', color: C_MUTED, fontStyle: 'italic' };
        },
        loaderStyle() {
            return {
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '32px',
            };
        },
    },
    methods: {
        // ===== c-design helpers =====
        cRadioDotStyle(checked) {
            return {
                width: '22px', height: '22px', borderRadius: '50%',
                border: `1.5px solid ${checked ? C_INK : C_HAIR}`,
                background: '#fff', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginTop: '1px',
                cursor: 'pointer',
            };
        },
        signedCardStyle(active) {
            return {
                padding: '24px 26px',
                border: `2px solid ${active ? C_INK : 'transparent'}`,
                cursor: 'pointer',
            };
        },
        proofRowStyle(active) {
            return {
                display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: '12px',
                alignItems: 'center',
                padding: '12px 14px',
                background: active ? C_CREAM : '#fff',
                border: `1.5px solid ${active ? C_INK : C_HAIR}`,
                borderRadius: '12px',
                cursor: 'pointer',
                position: 'relative',
            };
        },

        onNextPage() {
            if (this.is_from_query) {
                this.onChangeSection(
                    SECTIONS.THANK_YOU_UPLOAD_SOW,
                    SECTIONS.UPLOAD_LATER_SOW
                );
                return;
            }

            if (this.currectPageOfSOW === this.totalPagesOfSOW) {
                if (this.isHasKickOff) {
                    this.onChangeSection(
                        SECTIONS.EDIT_KICK_OFF_LINE_ITEM,
                        SECTIONS.UPLOAD_LATER_SOW
                    );
                    return;
                }

                this.onChangeSection(
                    SECTIONS.EDIT_SALE_LINE_ITEM,
                    SECTIONS.EDIT_KICK_OFF_LINE_ITEM
                );
                return;
            }

            this.setCurrentLineItem(this.currectPageOfSOW + 1);
            this.currectPageOfSOW = this.currectPageOfSOW + 1;

            this.$nextTick(() => {
                scrollToTop(); // Прокрутка вверх
            });
        },
        onPrevSection() {
            if (this.currentPageKickOff === 1) {
                this.onChangeSection(
                    SECTIONS.DASHBOARD,
                    SECTIONS.UPLOAD_LATER_SOW
                );
                return;
            }

            this.setCurrentLineItem(this.currectPageOfSOW - 1);
            this.currectPageOfSOW = this.currectPageOfSOW - 1;

            this.$nextTick(() => {
                scrollToTop(); // Прокрутка вверх
            });
        },
        onChangeDatePicker(e) {
            const date = e.target.value;

            if (date) {
                this.new_sow.date = date;
            }
        },
        onChangeValidDatePicker(e) {
            const date = e.target.value;

            if (date) {
                this.new_sow.valid_date = date;
            }
        },
        initDatePicker() {
            const node__datepicker = this.$refs.datepicker;

            if (!node__datepicker) {
                return;
            }

            let datepicker = null;
            const input = node__datepicker.querySelector("input");
            const button = node__datepicker.querySelector("button");

            if (input) {
                datepicker = new Datepicker(input, {
                    ...this.datepicker_options,
                });
            }

            if (button) {
                button.addEventListener("click", () => {
                    if (!datepicker.active) {
                        datepicker.show();
                    }
                });
            }
        },
        initValidDatePicker() {
            const node__datepicker = this.$refs.valid_datepicker;

            if (!node__datepicker) {
                return;
            }

            let datepicker = null;
            const input = node__datepicker.querySelector("input");
            const button = node__datepicker.querySelector("button");

            if (input) {
                datepicker = new Datepicker(input, {
                    ...this.datepicker_options,
                });
            }

            if (button) {
                button.addEventListener("click", () => {
                    if (!datepicker.active) {
                        datepicker.show();
                    }
                });
            }
        },
        initFilePicker() {
            const inputField = this.$refs.filepicker;

            if (!inputField) {
                return;
            }

            const button = inputField.querySelector("button");
            const input = inputField.querySelector("input");
            const filenameNode = inputField.querySelector("span.filename");

            if (button && input) {
                button.addEventListener("click", () => {
                    input.click();
                });
            }

            input.addEventListener("change", () => {
                let file = input.files;
                if (file && file[0]) {
                    const filename = file[0].name;
                    this.new_sow.filename = filename;
                    //filenameNode.innerHTML = filename;
                    this.new_sow.file = file[0];
                }
            });
        },
        pickFile() {
            let input = this.$refs.fileInput;
            let file = input.files;
            if (file && file[0]) {
                let reader = new FileReader();
                reader.readAsDataURL(file[0]);
                this.new_sow.file = file[0];
            }
        },
        setCurrentLineItem(index) {
            if (this.dealLineItemsWithouSOWs.length > 0) {
                const dealLineItems = this.dealLineItemsWithouSOWs;
                console.log("dealLineItems", dealLineItems);
                this.deal_id = dealLineItems[index - 1].deal.id;
                this.sow_id = dealLineItems[index - 1].id;
                this.deal_name = dealLineItems[index - 1].deal.name;
                this.new_sow.title = dealLineItems[index - 1].name;
                this.new_sow.date = dealLineItems[index - 1].date;
                this.new_sow.valid_date = dealLineItems[index - 1].valid_date;
            }
        },

        checkIfHaveDealLineItems(is_update = false) {
            if (this.dealLineItemsWithouSOWs.length > 0) {
                this.totalPagesOfSOW = this.dealLineItemsWithouSOWs.length;
                this.setCurrentLineItem(1);
            } else {
                if (this.isHasKickOff) {
                    this.onChangeSection(
                        SECTIONS.EDIT_KICK_OFF_LINE_ITEM,
                        SECTIONS.UPLOAD_LATER_SOW
                    );
                } else {
                    this.onChangeSection(
                        SECTIONS.EDIT_SALE_LINE_ITEM,
                        SECTIONS.UPLOAD_LATER_SOW
                    );
                }
            }
        },

        transformObjectToArray(obj) {
            return Object.values(obj).flatMap((row) =>
                Object.values(row["data"])
            );
        },

        async getData(is_update = false) {
            this.isLoading = true;

            await axios
                .post("/api/wizard/getList", {
                    page: "get_upload_later",
                })
                .then((res) => {
                    this.dealLineItemsWithouSOWs = this.transformObjectToArray(
                        Object.values(res.data.page)
                    );

                    if (this.getInitParamsFromQuery) {
                        if (this.getInitParamsFromQuery) {
                            const { sow_id } = this.getInitParamsFromQuery;

                            if (!sow_id) {
                                this.$toast.error("Not found deal");
                                return;
                            }

                            this.is_from_query = true;

                            const sow_data = this.dealLineItemsWithouSOWs.find(
                                (dli) => dli.id === Number(sow_id)
                            );
                            if (sow_data) {
                                this.deal_id = sow_data.deal.id;
                                this.deal_name = sow_data.deal.name;
                                this.sow_id = sow_data.id;
                                this.new_sow.title = sow_data.name || "";
                                this.new_sow.date = sow_data.date || null;
                                this.new_sow.valid_date =
                                    sow_data.valid_date || null;
                            }

                            if (!sow_data) {
                                this.onChangeSection(
                                    SECTIONS.THANK_YOU_UPLOAD_SOW,
                                    SECTIONS.UPLOAD_LATER_SOW
                                );
                            }
                        }
                        return;
                    }

                    this.checkIfHaveDealLineItems();
                    this.$nextTick(() => {
                        scrollToTop(); // Прокрутка вверх
                    });
                })
                .catch((err) => {
                    console.log("err getData upsell", err);
                    this.onChangeSection(
                        SECTIONS.DASHBOARD,
                        SECTIONS.UPLOAD_LATER_SOW
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        validateFields() {
            this.errors.date = null;
            this.errors.title = null;
            this.errors.file = null;
            this.errors.type = null;
            this.errors.url = null;
            this.errors.valid_date = null;

            if (!this.new_sow.title) {
                this.errors.title = "Required";
            }

            if (!this.new_sow.date) {
                this.errors.date = "Required";
            }

            if (
                this.is_file_option &&
                !this.new_sow.file &&
                this.is_has_signed_sow
            ) {
                this.errors.file = "Required";
            }

            if (
                !this.is_file_option &&
                !this.new_sow.url &&
                this.is_has_signed_sow
            ) {
                this.errors.url = "Required";
            }
            if (
                this.is_has_signed_sow &&
                !this.is_file_option &&
                this.new_sow.url &&
                !this.urlRegex.test(this.new_sow.url)
            ) {
                this.errors.url = "Invalid URL";
            }
            if (
                this.new_sow.date &&
                this.new_sow.valid_date &&
                new Date(this.new_sow.date) > new Date(this.new_sow.valid_date)
            ) {
                this.errors.valid_date = "Invalid valid date";
            }
            if (
                !this.is_has_signed_sow &&
                this.new_sow.description.length < 5
            ) {
                this.errors.description = "Required";
            }
        },

        async onSaveSow() {
            if (this.is_file_option === "later") this.onNextPage();

            try {
                this.validateFields();

                if (
                    this.is_has_signed_sow &&
                    (this.errors.date ||
                        this.errors.file ||
                        this.errors.type ||
                        this.errors.url ||
                        this.errors.valid_date)
                ) {
                    return;
                }

                if (
                    !this.is_has_signed_sow &&
                    (this.errors.title ||
                        this.errors.date ||
                        this.errors.valid_date)
                ) {
                    return;
                }
                this.isLoading = true;
                const formData = new FormData();

                formData.append("name", this.new_sow.title);
                formData.append("id", this.sow_id);
                formData.append("deal_id", this.deal_id);
                formData.append(
                    "file",
                    this.new_sow.file === null ? "" : this.new_sow.file
                );
                formData.append(
                    "date",
                    this.new_sow.date === null ? "" : this.new_sow.date
                );
                formData.append(
                    "valid_date",
                    this.new_sow.valid_date === null
                        ? ""
                        : this.new_sow.valid_date
                );
                formData.append("description", this.new_sow.description);
                formData.append(
                    "url",
                    this.new_sow.url ? this.new_sow.url : ""
                );
                formData.append(
                    "type",
                    this.is_has_signed_sow
                        ? this.is_file_option
                            ? "file"
                            : "url"
                        : "none"
                );

                await axios
                    .post("/deals/sow/update", formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then(() => {
                        this.onNextPage();
                    })
                    .catch((err) => {
                        this.$toast.error("Something went wrong!");
                        console.log("ERROR addSOWFile", err);
                    })
                    .finally(() => (this.isLoading = false));
            } catch (err) {
                console.error("Error in UploadLater.vue - onSaveSow", err);
            }
        },

        onSetUploadLater() {
            this.is_file_option = null;
            this.is_upload_later = true;
        },
    },
};
</script>
